import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';
import CustomerPicker from './CustomerPicker';
import CustomerHistoryPanel from './CustomerHistoryPanel';
import { useAuth } from '../context/AuthContext';
import DatePicker, { getTodayISO } from './DatePicker';
import AssigneeMultiSelect from './AssigneeMultiSelect';
import MentionTextarea from './MentionTextarea';
import TeamSelect from './TeamSelect';
import { CASE_GLASSES_PAIRED_OPTIONS, PRODUCT_TYPE_OPTIONS, GLASSES_VERSION_OPTIONS, LENS_TYPE_OPTIONS, LABEL_OPTIONS } from '../constants/orderFieldOptions';
import { getPref, setPref, PREF_KEYS } from '../utils/preferences';

const CreateTicketModal = ({ board, onClose, onCreated }) => {
  const { employee } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [labels, setLabels] = useState('');
  const [dueDate, setDueDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10); });
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [reportToIds, setReportToIds] = useState([]);
  const [descriptionTaggedIds, setDescriptionTaggedIds] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Defaults to the board's own first column (its normal starting status),
  // but lets you drop a ticket straight onto any status -- e.g. an order
  // that's already been assembled by the time it's logged here.
  const sortedColumns = [...board.columns].sort((a, b) => a.order - b.order);
  const [columnId, setColumnId] = useState(sortedColumns[0]?.id ?? null);

  const activeTeam = teamId ? teams.find((t) => t.id === teamId) : null;
  // Team only narrows the *Assignees* picker (who's doing the work) --
  // Report To is deliberately left showing everyone, since who a ticket
  // escalates/reports to (e.g. a manager) is often outside the team doing
  // the work. Must never hide someone already picked just because they're
  // outside the team, so the assignee picker adds back its own current
  // selections on top of the team-filtered list.
  const assigneePickerEmployees = (selectedIds) => {
    if (!activeTeam) return employees;
    const inTeam = employees.filter((e) => activeTeam.members.some((m) => m.mavis_user_id === e.mavis_user_id));
    const missingSelected = employees.filter((e) => selectedIds.includes(e.mavis_user_id) && !inTeam.includes(e));
    return [...inTeam, ...missingSelected];
  };

  const isOrderBoard = board.key === 'OF';
  // Remembers whatever you last left this checkbox at, so it doesn't
  // reset to unchecked on every new ticket -- once you've checked it,
  // new tickets default to checked too, until you uncheck it again.
  const [notifyCustomer, setNotifyCustomer] = useState(() => getPref(PREF_KEYS.NOTIFY_CUSTOMER_DEFAULT, false));
  const handleNotifyCustomerChange = (checked) => {
    setNotifyCustomer(checked);
    setPref(PREF_KEYS.NOTIFY_CUSTOMER_DEFAULT, checked);
  };
  const [orderFields, setOrderFields] = useState({
    order_number: '', product_type: '', case_and_glasses_paired: '',
    glasses_version: '', lens_type: '', tracking_number: '',
  });

  useEffect(() => {
    api.getEmployees().then(setEmployees).catch(() => {});
    api.getTeams().then(setTeams).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        board: board.key,
        column: columnId || undefined,
        title: title.trim(),
        description,
        priority,
        labels,
        due_date: dueDate || null,
        assignee_ids: assigneeIds,
        report_to_ids: reportToIds,
        description_tagged_employee_ids: descriptionTaggedIds,
        customer_id: customer ? customer.id : null,
        notify_customer: customer ? notifyCustomer : false,
        // Tracking number applies to any board, not just Order Fulfilment
        // -- the rest of orderFields (order_number, product_type, etc.)
        // stays Order Fulfilment-only.
        tracking_number: orderFields.tracking_number,
        ...(isOrderBoard ? orderFields : {}),
      };
      const ticket = await api.createTicket(payload);
      toast.success(`Created ${ticket.key}`);
      onCreated(ticket);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>New ticket &middot; {board.name}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="ct-title">Title <span className="required">*</span></label>
            <input id="ct-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus disabled={submitting} />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="ct-desc">Description</label>
            <MentionTextarea
              id="ct-desc"
              minHeight={90}
              placeholder="Type @ to tag someone"
              value={description}
              onChange={setDescription}
              employees={employees}
              taggedIds={descriptionTaggedIds}
              onTaggedIdsChange={setDescriptionTaggedIds}
              disabled={submitting}
            />
          </div>

          {isOrderBoard && (
            <div className="form-group">
              <label className="form-label" htmlFor="ct-order-number">Order number</label>
              <input id="ct-order-number" className="form-input" placeholder="e.g. 1509" value={orderFields.order_number}
                onChange={(e) => setOrderFields((p) => ({ ...p, order_number: e.target.value }))} disabled={submitting} />
            </div>
          )}

          <div className="field-row">
            <div className="form-group">
              <label className="form-label" htmlFor="ct-status">Status</label>
              <select id="ct-status" className="form-select" value={columnId || ''} onChange={(e) => setColumnId(Number(e.target.value))} disabled={submitting}>
                {sortedColumns.map((col) => <option key={col.id} value={col.id}>{col.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ct-priority">Priority</label>
              <select id="ct-priority" className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)} disabled={submitting}>
                <option value="highest">Highest</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="lowest">Lowest</option>
              </select>
            </div>
          </div>

          <div className="field-row">
            <div className="form-group">
              <label className="form-label" htmlFor="ct-due">Due date</label>
              {employee?.can_edit_due_date ? (
                <DatePicker id="ct-due" value={dueDate} onChange={(v) => setDueDate(v || '')} disabled={submitting} />
              ) : (
                <div className="form-input" style={{ color: '#aab7c4' }}>Set automatically: 3 days from now (only David can change it)</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ct-labels">Label</label>
              <select id="ct-labels" className="form-select" value={labels} onChange={(e) => setLabels(e.target.value)} disabled={submitting}>
                <option value="">—</option>
                {LABEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div className="field-row">
            <div className="form-group">
              <label className="form-label" htmlFor="ct-team">Team</label>
              <TeamSelect id="ct-team" teams={teams} teamId={teamId} onChange={setTeamId} disabled={submitting} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="ct-assignee">Assignees</label>
            <AssigneeMultiSelect id="ct-assignee" employees={assigneePickerEmployees(assigneeIds)} selectedIds={assigneeIds} onChange={setAssigneeIds} disabled={submitting} />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="ct-report-to">Report to</label>
            <AssigneeMultiSelect id="ct-report-to" employees={employees} selectedIds={reportToIds} onChange={setReportToIds} disabled={submitting} />
          </div>

          <div className="section-title">Customer</div>
          <CustomerPicker selectedCustomer={customer} onSelect={setCustomer} />
          {customer && customer.id && <CustomerHistoryPanel customerId={customer.id} mode="create" />}
          {customer && (
            <div className="checkbox-row">
              <input
                type="checkbox"
                id="ct-notify-customer"
                checked={notifyCustomer}
                onChange={(e) => handleNotifyCustomerChange(e.target.checked)}
              />
              <label htmlFor="ct-notify-customer">Notify customer by email about status changes and replies</label>
            </div>
          )}

          <div className="section-title">Tracking</div>
          <div className="form-group">
            <label className="form-label" htmlFor="ct-tracking-number">Tracking number</label>
            <input id="ct-tracking-number" className="form-input" value={orderFields.tracking_number}
              onChange={(e) => setOrderFields((p) => ({ ...p, tracking_number: e.target.value }))} disabled={submitting} />
          </div>

          {isOrderBoard && (
            <>
              <div className="section-title">Order details</div>
              <div className="field-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="ct-case-glasses-paired">Case and Glasses Paired</label>
                  <select id="ct-case-glasses-paired" className="form-select" value={orderFields.case_and_glasses_paired}
                    onChange={(e) => setOrderFields((p) => ({ ...p, case_and_glasses_paired: e.target.value }))} disabled={submitting}>
                    <option value="">—</option>
                    {CASE_GLASSES_PAIRED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="ct-product-type">Product type</label>
                  <select id="ct-product-type" className="form-select" value={orderFields.product_type}
                    onChange={(e) => setOrderFields((p) => ({ ...p, product_type: e.target.value }))} disabled={submitting}>
                    <option value="">—</option>
                    {PRODUCT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="ct-glasses-version">Glasses version</label>
                  <select id="ct-glasses-version" className="form-select" value={orderFields.glasses_version}
                    onChange={(e) => setOrderFields((p) => ({ ...p, glasses_version: e.target.value }))} disabled={submitting}>
                    <option value="">—</option>
                    {GLASSES_VERSION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="ct-lens-type">Lens type</label>
                  <select id="ct-lens-type" className="form-select" value={orderFields.lens_type}
                    onChange={(e) => setOrderFields((p) => ({ ...p, lens_type: e.target.value }))} disabled={submitting}>
                    <option value="">—</option>
                    {LENS_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create ticket'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicketModal;
